// Full purchase history page. Paddle-only for now — Razorpay is hidden in the UI.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Receipt,
  Copy,
  Check,
  MoreHorizontal,
  Mail,
  RotateCcw,
  Sparkles,
  TrendingUp,
  MessageCircle,
} from "lucide-react";
import QRCode from "qrcode";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { StitchPage, StitchPanel, Reveal } from "@/components/stitch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  formatPurchaseAmount,
  groupByMonth,
  loadPurchases,
  maskReference,
  type PurchaseRow,
  type PurchaseStatus,
  type PurchaseSummary,
} from "@/lib/purchases";
import { cn } from "@/lib/utils";

const SUPPORT_EMAIL = "support@fitauraai.com";
const WHATSAPP_PHONE = "918328261165";
const WHATSAPP_QR_LINK = "https://wa.me/message/SOUKJ4YQCE5SC1";

type Filter = "all" | "paid" | "pending" | "refunded";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "pending", label: "Pending" },
  { id: "refunded", label: "Refunds" },
];

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const STATUS_CLASSES: Record<PurchaseStatus, string> = {
  paid: "bg-success/15 text-success border-success/30",
  pending: "bg-muted text-muted-foreground border-white/10",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  refunded: "bg-accent/15 text-accent border-accent/30",
};

export default function Purchases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PurchaseRow[]>([]);
  const [summary, setSummary] = useState<PurchaseSummary | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [buyOpen, setBuyOpen] = useState(false);
  const [copiedRef, setCopiedRef] = useState<string | null>(null);
  const [reportRow, setReportRow] = useState<PurchaseRow | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    loadPurchases(user.id)
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setSummary(res.summary);
      })
      .catch((e) => {
        if (cancelled) return;
        toast({
          title: "Couldn't load purchases",
          description: e instanceof Error ? e.message : "Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, toast]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const grouped = useMemo(() => groupByMonth(filtered), [filtered]);

  const copyReference = async (ref: string) => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopiedRef(ref);
      toast({ title: "Reference copied" });
      setTimeout(() => setCopiedRef((cur) => (cur === ref ? null : cur)), 1600);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const reportIssue = (row: PurchaseRow) => {
    setReportRow(row);
  };

  const body = (
    <div className="mx-auto w-full max-w-2xl px-5 pt-6 pb-12">
          {/* Header */}
          <Reveal y={8} duration={0.4}>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <h1 className="font-display text-2xl font-bold uppercase italic tracking-tight text-foreground">
                  Purchase History
                </h1>
                <p className="text-xs text-muted-foreground">
                  All credit purchases, refunds, and invoices.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Summary card */}
          <Reveal y={8} duration={0.4} delay={0.05}>
            <StitchPanel
              className="mt-5 grid grid-cols-3 gap-3 overflow-hidden p-5"
              brackets={false}
            >
              <SummaryStat
                label="Total spent"
                value={
                  loading || !summary
                    ? "—"
                    : formatPurchaseAmount(summary.totalSpent, summary.summaryCurrency)
                }
                icon={<TrendingUp className="h-4 w-4" />}
              />
              <SummaryStat
                label="Credits bought"
                value={loading || !summary ? "—" : String(summary.totalCredits)}
                icon={<Sparkles className="h-4 w-4" />}
              />
              <SummaryStat
                label="Purchases"
                value={
                  loading || !summary ? "—" : String(summary.totalPurchases)
                }
                icon={<Receipt className="h-4 w-4" />}
              />
            </StitchPanel>
          </Reveal>

          {/* Per-currency breakdown — only shown when user has paid in 2+ currencies. */}
          {!loading && summary && summary.byCurrency.length > 1 && (
            <Reveal y={8} duration={0.4} delay={0.07}>
              <StitchPanel className="mt-3 p-4" brackets={false}>
                <p className="font-display mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  Spent by currency
                </p>
                <ul className="space-y-2">
                  {summary.byCurrency.map((c) => (
                    <li
                      key={c.currency}
                      className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="rounded-md border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground/80">
                          {c.currency}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {c.purchases} {c.purchases === 1 ? "purchase" : "purchases"}
                        </span>
                      </div>
                      <span className="font-display text-sm font-bold tabular-nums text-foreground">
                        {formatPurchaseAmount(c.amount, c.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </StitchPanel>
            </Reveal>
          )}

          {/* Filter chips */}
          <Reveal y={8} duration={0.4} delay={0.1}>
            <div className="mt-5 flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const count =
                  f.id === "all"
                    ? rows.length
                    : rows.filter((r) => r.status === f.id).length;
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-semibold transition-all",
                      active
                        ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary))]"
                        : "border-white/10 bg-white/[0.03] text-muted-foreground hover:border-primary/40 hover:text-foreground",
                    )}
                  >
                    {f.label}
                    <span className={cn("ml-1.5 tabular-nums", active ? "opacity-90" : "opacity-60")}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* List */}
          <div className="mt-5 space-y-6">
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-2xl" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState onBuy={() => setBuyOpen(true)} hasAny={rows.length > 0} />
            ) : (
              grouped.map(({ label, rows: monthRows }) => (
                <section key={label}>
                  <h2 className="font-display mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    {label}
                  </h2>
                  <ul className="space-y-2">
                    {monthRows.map((r) => (
                    <PurchaseRowCard
                      key={r.id}
                      row={r}
                      copied={copiedRef === r.reference}
                      onCopy={() => copyReference(r.reference)}
                      onReport={() => reportIssue(r)}
                    />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>

          <p className="mt-10 text-center text-[10px] text-muted-foreground/70">
            Need a refund or have a billing question? Email{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="underline hover:text-foreground"
            >
              {SUPPORT_EMAIL}
            </a>
          .
          </p>
        </div>
  );

  return (
    <>
      <SEO noindex />
      <StitchPage glow={false}>
        {isMobile ? <MobileShell title="Purchases">{body}</MobileShell> : body}
        <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
        <ReportIssueDialog row={reportRow} onClose={() => setReportRow(null)} />
      </StitchPage>
    </>
  );
}

const SummaryStat = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) => (
  <div className="min-w-0">
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary ring-1 ring-primary/20">
      {icon}
    </div>
    <p className="font-display mt-2 truncate text-xl font-bold tabular-nums text-foreground">
      {value}
    </p>
    <p className="font-display mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </p>
  </div>
);

const PurchaseRowCard = ({
  row,
  copied,
  onCopy,
  onReport,
}: {
  row: PurchaseRow;
  copied: boolean;
  onCopy: () => void;
  onReport: () => void;
}) => {
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl transition-colors hover:border-primary/30">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-display text-lg font-bold tabular-nums text-foreground">
              {formatPurchaseAmount(row.amount, row.currency)}
            </span>
            {row.amount > 0 && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {row.currency}
              </span>
            )}
            <span className="text-xs text-muted-foreground">·</span>
            <span
              className={cn(
                "text-sm font-semibold tabular-nums",
                row.credits > 0 ? "text-foreground" : "text-accent",
              )}
            >
              {row.credits > 0 ? `+${row.credits}` : row.credits} credits
            </span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span>{formatDateTime(row.createdAt)}</span>
            <span>·</span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/80">
              {row.provider === "System" ? "Credit refund" : row.provider}
            </span>
            {row.packId && row.kind === "purchase" && (
              <>
                <span>·</span>
                <span className="font-mono">{row.packId}</span>
              </>
            )}
            <span>·</span>
            <span className="font-mono text-foreground/70">
              {maskReference(row.reference)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              STATUS_CLASSES[row.status],
            )}
          >
            {row.status === "paid" && <Check className="h-2.5 w-2.5" />}
            {row.status === "refunded" && <RotateCcw className="h-2.5 w-2.5" />}
            {row.status}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                aria-label="Actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={onCopy}>
                {copied ? (
                  <Check className="mr-2 h-4 w-4 text-success" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                Copy reference
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onReport}>
                <Mail className="mr-2 h-4 w-4" />
                Report issue
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </li>
  );
};

const EmptyState = ({
  onBuy,
  hasAny,
}: {
  onBuy: () => void;
  hasAny: boolean;
}) => (
  <StitchPanel className="p-10 text-center" brackets={false}>
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary ring-1 ring-primary/20">
      <Receipt className="h-6 w-6" />
    </div>
    <p className="font-display text-base font-bold uppercase tracking-tight text-foreground">
      {hasAny ? "Nothing matches this filter" : "No purchases yet"}
    </p>
    <p className="mt-1.5 text-sm text-muted-foreground">
      {hasAny
        ? "Try a different filter to see your other transactions."
        : "Buy a credit pack to power try-ons, outfits, and Aura scoring."}
    </p>
    {!hasAny && (
      <Button
        onClick={onBuy}
        className="font-display mt-5 bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_24px_-6px_hsl(var(--primary))] hover:opacity-95"
      >
        Buy credits
      </Button>
    )}
    {hasAny && (
      <Link
        to="/app"
        className="mt-5 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary hover:underline"
      >
        Back to app
      </Link>
    )}
  </StitchPanel>
);

const ReportIssueDialog = ({
  row,
  onClose,
}: {
  row: PurchaseRow | null;
  onClose: () => void;
}) => {
  const { toast } = useToast();
  const [copiedDetails, setCopiedDetails] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const open = row !== null;

  const body = row
    ? `Hi,\n\nI need help with the following purchase:\n\nReference: ${row.reference}\nDate: ${formatDateTime(row.createdAt)}\nAmount: ${formatPurchaseAmount(row.amount, row.currency)} ${row.currency}\nCredits: ${row.credits}\n\nDescribe the issue here:\n`
    : "";

  // Auto-copy the message body when the dialog opens.
  useEffect(() => {
    if (!open) {
      setCopiedDetails(false);
      setCopiedEmail(false);
      return;
    }
    navigator.clipboard?.writeText(body).then(
      () => setCopiedDetails(true),
      () => {
        /* clipboard may be blocked — silent */
      },
    );
  }, [open, body]);

  // Generate the WhatsApp QR once when the dialog opens.
  useEffect(() => {
    if (!open) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(WHATSAPP_QR_LINK, { width: 200, margin: 2 })
      .then((url) => setQrDataUrl(url))
      .catch(() => setQrDataUrl(null));
  }, [open]);

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopiedDetails(true);
      toast({ title: "Details copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopiedEmail(true);
      toast({ title: "Email copied" });
      setTimeout(() => setCopiedEmail(false), 1600);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-background">
        <DialogHeader>
          <DialogTitle className="font-display uppercase italic tracking-tight">
            Report an issue
          </DialogTitle>
          <DialogDescription>
            We'll get back to you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {row && (
          <>
            {/* Summary block */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground">Reference</dt>
                <dd className="break-all font-mono text-foreground">{row.reference}</dd>
                <dt className="text-muted-foreground">Date</dt>
                <dd className="text-foreground">{formatDateTime(row.createdAt)}</dd>
                <dt className="text-muted-foreground">Amount</dt>
                <dd className="tabular-nums text-foreground">
                  {formatPurchaseAmount(row.amount, row.currency)} {row.currency}
                </dd>
                <dt className="text-muted-foreground">Credits</dt>
                <dd className="tabular-nums text-foreground">{row.credits}</dd>
              </dl>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyDetails}
                className="mt-3 h-8 w-full border-white/10 bg-white/[0.03] text-xs"
              >
                {copiedDetails ? (
                  <Check className="mr-2 h-3.5 w-3.5 text-success" />
                ) : (
                  <Copy className="mr-2 h-3.5 w-3.5" />
                )}
                {copiedDetails ? "Details copied" : "Copy details"}
              </Button>
            </div>

            {copiedDetails && (
              <p className="-mt-1 text-center text-[11px] text-muted-foreground">
                Message copied — paste it into your reply.
              </p>
            )}

            {/* Email — kept as the primary contact action */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[12px] text-muted-foreground">
              <Mail className="h-3.5 w-3.5" />
              <span>Email us at</span>
              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex items-center gap-1 font-semibold text-foreground hover:underline"
              >
                {SUPPORT_EMAIL}
                {copiedEmail ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Copy className="h-3 w-3 opacity-60" />
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">
              <span className="h-px flex-1 bg-white/10" />
              or chat on whatsapp
              <span className="h-px flex-1 bg-white/10" />
            </div>

            {/* WhatsApp QR — desktop scans, mobile taps the button below */}
            <div className="flex flex-col items-center rounded-xl border border-primary/30 bg-primary/5 p-4">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="WhatsApp QR Code"
                  className="h-40 w-40 rounded-lg bg-white p-2"
                />
              ) : (
                <Skeleton className="h-40 w-40 rounded-lg" />
              )}
              <p className="mt-3 text-center text-[11px] text-muted-foreground">
                Scan with your phone camera to chat with support
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
